import React, {Fragment} from 'react';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';
import {Box, Button, Card, CircularProgress, Stack, Typography, useTheme} from '@mui/material';
import {
	DecisionModal,
	MainFooter,
	MainHeader,
	PlanTypeChip,
	Snackbar,
	StyledGrid,
} from 'presentation/components';
import {HomeEnum, SelectionFlowEnum} from 'presentation/constants';
import Icon from 'presentation/icons';

import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';

import {AccessRow, ConsentTermDrawer, FeaturesVisibilityDrawer} from '../../components';
import {useDataSharing} from './hooks/useDataSharing';

const {
	title,
	unblockedAccess,
	blockedAccess,
	featuresLink,
	consentTerm,
	blockTitle,
	warning,
	blockPermissionDescription,
	back,
	blockAccess,
	planLabel,
	beneficiariesLabel,
	accessTitle,
	accessDescription,
	submitErrorTitle,
	submitErrorDescription,
	emptyStateTitle,
	emptyStateDescription,
	loadErrorTitle,
	loadErrorDescription,
	tryAgain,
} = translations['pt-br'].privateFlow.profile.dataSharing;

const {changePlan} = translations['pt-br'].commons;
const {understood} = translations['pt-br'].commons;

export function DataSharing() {
	const {pathname} = useLocation();
	const theme = useTheme();
	const navigate = useNavigate();

	const {
		isShareAccessEnabled,
		canChangeAccess,
		isLaptop,
		beneficiary,
		selectedPlan,
		dependents,
		isLoading,
		isError,
		refetch,
		isPostingPermission,
		handlePermissionChange,
		postPendingPermission,
		handleBack,
		handleShowFeatures,
		showConsentDrawer,
		showReadOnlyConsentDrawer,
		showFeaturesDrawer,
		showSubmitError,
		successMessage,
		setShowSubmitError,
		setSuccessMessage,
		setShowConsentDrawer,
		setShowReadOnlyConsentDrawer,
		setShowFeaturesDrawer,
		pendingItem,
		setPendingItem,
	} = useDataSharing();

	if (!isShareAccessEnabled || !canChangeAccess) {
		return <Navigate to={HomeEnum.HOME_PAGE} replace />;
	}

	const renderBeneficiaries = () => {
		if (isLoading) {
			return (
				<Stack alignItems="center" py={2}>
					<CircularProgress size={24} />
				</Stack>
			);
		}

		if (isError) {
			return (
				<Stack alignItems="center" gap={1} py={2} textAlign="center">
					<Typography variant="body1" fontWeight={600}>
						{loadErrorTitle}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{loadErrorDescription}
					</Typography>
					<Button variant="text" onClick={() => refetch()}>
						{tryAgain}
					</Button>
				</Stack>
			);
		}

		if (dependents.length === 0) {
			return (
				<Stack alignItems="center" gap={1} py={2} textAlign="center">
					<Typography variant="body1" fontWeight={600}>
						{emptyStateTitle}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{emptyStateDescription}
					</Typography>
				</Stack>
			);
		}

		return dependents.map(dependent => (
			<AccessRow
				key={`${dependent.cdPessoaMembro}-${dependent.cdUsuarioMembro}-${dependent.cdPlanoMembro}`}
				item={dependent}
				checked={dependent.flPermitido === 'S'}
				disabled={isPostingPermission}
				description={dependent.flPermitido === 'S' ? unblockedAccess : blockedAccess}
				onChange={value => handlePermissionChange(dependent, value)}
			/>
		));
	};

	return (
		<Fragment>
			<MainHeader
				title={title}
				handleBack={handleBack}
				handleClose={handleBack}
				variant="subRoute"
			/>

			<Stack padding={isLaptop ? 2 : 4} flex={1}>
				<StyledGrid.Root>
					<StyledGrid.Item xs={12} md={10} lg={10}>
						<Stack gap={3}>
							<Card elevation={0} sx={{p: 2, borderRadius: 1}}>
								<Stack gap={2}>
									<Stack>
										<Typography variant="body1" fontWeight={600}>
											{accessTitle}
										</Typography>
										<Typography variant="body2">{accessDescription}</Typography>
									</Stack>

									<Card
										elevation={0}
										sx={{
											border: `1px solid ${theme.palette.divider}`,
											p: 2,
											borderRadius: 1,
										}}>
										<Stack gap={0.5}>
											<Typography
												variant="body2"
												color="text.secondary"
												fontWeight={600}>
												{planLabel}
											</Typography>
											<Typography variant="body1">
												{formatText(
													selectedPlan?.nmPlano || beneficiary.nmPlano,
												)}
											</Typography>

											<Stack
												direction="row"
												alignItems="center"
												justifyContent="space-between">
												<PlanTypeChip />
												<Button
													variant="text"
													size="small"
													onClick={() =>
														navigate(SelectionFlowEnum.PLAN_SELECTION, {
															state: {pathFrom: pathname},
														})
													}>
													{changePlan}
												</Button>
											</Stack>
										</Stack>
									</Card>

									<Stack gap={1}>
										<Typography
											variant="body1"
											fontWeight={600}
											color="primary">
											{beneficiariesLabel} ({dependents.length})
										</Typography>
										{renderBeneficiaries()}
									</Stack>

									<Stack alignItems="center">
										<Button variant="text" onClick={handleShowFeatures}>
											{featuresLink}
										</Button>
									</Stack>
								</Stack>
							</Card>

							<Card
								role="button"
								tabIndex={0}
								onKeyDown={event => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										setShowReadOnlyConsentDrawer(true);
									}
								}}
								elevation={0}
								sx={{p: 2, borderRadius: 1, cursor: 'pointer'}}
								onClick={() => setShowReadOnlyConsentDrawer(true)}>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between">
									<Typography variant="body2">{consentTerm}</Typography>
									<Icon
										name="arrow-right-filled"
										color={theme.palette.primary.main}
										size={20}
									/>
								</Stack>
							</Card>
						</Stack>
					</StyledGrid.Item>
				</StyledGrid.Root>
			</Stack>

			<Box mt="auto">
				<MainFooter />
			</Box>

			<DecisionModal
				open={Boolean(pendingItem && !showConsentDrawer)}
				onClose={() => setPendingItem(null)}
				headerTitle={warning}
				customIconName="info-filled"
				title={blockTitle}
				description={blockPermissionDescription}
				firstButton={{
					title: blockAccess,
					handleClick: () => postPendingPermission(false),
					variant: 'contained',
				}}
				secondButton={{
					title: back,
					handleClick: () => setPendingItem(null),
					variant: 'outlined',
				}}
				isFullScreen={false}
				maxWidth={420}
			/>

			<DecisionModal
				open={showSubmitError}
				onClose={() => setShowSubmitError(false)}
				headerTitle={warning}
				customIconName="info-filled"
				title={submitErrorTitle}
				description={submitErrorDescription}
				firstButton={{
					title: understood,
					handleClick: () => setShowSubmitError(false),
					variant: 'contained',
				}}
				isFullScreen={false}
				maxWidth={420}
			/>

			<ConsentTermDrawer
				open={showConsentDrawer || showReadOnlyConsentDrawer}
				readonly={showReadOnlyConsentDrawer}
				onClose={() => {
					setShowConsentDrawer(false);
					setShowReadOnlyConsentDrawer(false);
					setPendingItem(null);
				}}
				onAccept={() => {
					if (showReadOnlyConsentDrawer) {
						setShowReadOnlyConsentDrawer(false);
						return;
					}
					setShowConsentDrawer(false);
					postPendingPermission(true);
				}}
			/>

			<FeaturesVisibilityDrawer
				open={showFeaturesDrawer}
				onClose={() => setShowFeaturesDrawer(false)}
			/>

			<Snackbar
				open={Boolean(successMessage)}
				message={successMessage}
				color={theme.palette.success.main}
				onClose={() => setSuccessMessage('')}
			/>
		</Fragment>
	);
}
